/**
 * Algolia Cache Configuration
 * Provides centralized caching configuration for all Algolia clients.
 *
 * Uses Algolia SDK's built-in localStorage cache for response caching.
 * Bump CACHE_VERSION to invalidate all users' caches after updates.
 *
 * Features:
 * - TTL-based expiration (default 30 min) so stale data auto-expires
 * - Expired entry sweep on init
 *
 * @see https://www.algolia.com/doc/libraries/sdk/caching
 */
const AlgoliaCacheConfig = {
  // Bump this to invalidate all cached responses
  CACHE_VERSION: 2,

  // Algolia App ID (used in cache key)
  APP_ID: "2U4DEZQTYN",

  // Cache TTL in milliseconds (30 minutes)
  TTL_MS: 30 * 60 * 1000,

  /**
   * Check if cache should be bypassed via URL param
   * @returns {boolean}
   */
  shouldBypassCache() {
    const params = new URLSearchParams(window.location.search);
    return params.has("nocache");
  },

  /**
   * Get the cache key prefix for localStorage
   * @returns {string}
   */
  getCacheKey() {
    return `algolia-responses-v${this.CACHE_VERSION}-${this.APP_ID}`;
  },

  /**
   * Create a localStorage cache for Algolia responses
   * Falls back to in-memory cache if localStorage unavailable
   * @returns {object} Cache object compatible with Algolia SDK
   */
  createResponsesCache() {
    const cacheKey = this.getCacheKey();
    const bypassCache = this.shouldBypassCache();

    // If bypass requested, return null cache (no caching)
    if (bypassCache) {
      return this.createNullCache();
    }

    // Try localStorage, fall back to memory cache
    try {
      if (typeof localStorage === "undefined") {
        return this.createMemoryCache();
      }
      // Test localStorage availability
      localStorage.setItem("__algolia_test__", "1");
      localStorage.removeItem("__algolia_test__");

      // Clean expired entries on init
      this.evictExpired(cacheKey);

      return this.createLocalStorageCache(cacheKey);
    } catch (e) {
      // localStorage not available (private browsing, etc.)
      return this.createMemoryCache();
    }
  },

  /**
   * Create localStorage-based cache with TTL
   * @param {string} key - Cache key prefix
   * @returns {object} Cache object
   */
  createLocalStorageCache(key) {
    const self = this;
    return {
      get(cacheKey, defaultValue, events = {}) {
        return Promise.resolve().then(() => {
          const keyStr = JSON.stringify(cacheKey);
          const storageKey = `${key}_${keyStr}`;
          const stored = localStorage.getItem(storageKey);

          if (stored !== null) {
            try {
              const entry = JSON.parse(stored);

              // Check TTL — if entry has a timestamp and it's expired, remove it
              if (entry && entry.__ts) {
                const ttl = self.TTL_MS;
                if (Date.now() - entry.__ts > ttl) {
                  localStorage.removeItem(storageKey);
                  // Fall through to defaultValue (cache miss)
                } else {
                  // Valid cache hit — return the data
                  return entry.__data;
                }
              } else {
                // Legacy entry without TTL metadata — treat as valid but wrap on next set
                return entry;
              }
            } catch (e) {
              console.warn("[AlgoliaCacheConfig] Corrupted cache entry, removing");
              localStorage.removeItem(storageKey);
            }
          }

          const value = typeof defaultValue === "function" ? defaultValue() : defaultValue;

          return Promise.resolve(value).then((resolvedValue) => {
            if (events.miss) {
              events.miss(resolvedValue);
            }
            return resolvedValue;
          });
        });
      },

      set(cacheKey, value) {
        return Promise.resolve().then(() => {
          const keyStr = JSON.stringify(cacheKey);
          const storageKey = `${key}_${keyStr}`;
          const entry = { __ts: Date.now(), __data: value };

          try {
            localStorage.setItem(storageKey, JSON.stringify(entry));
          } catch (e) {
            if (e.name === "QuotaExceededError") {
              // Clear all Algolia cache and retry once
              self.clearAll();
              try {
                localStorage.setItem(storageKey, JSON.stringify(entry));
              } catch (retryError) {
                console.warn("[AlgoliaCacheConfig] localStorage quota exceeded, caching disabled");
              }
            }
          }
          return value;
        });
      },

      delete(cacheKey) {
        return Promise.resolve().then(() => {
          const keyStr = JSON.stringify(cacheKey);
          localStorage.removeItem(`${key}_${keyStr}`);
        });
      },

      clear() {
        return Promise.resolve().then(() => {
          // Only clear our cache keys, not all localStorage
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const storageKey = localStorage.key(i);
            if (storageKey && storageKey.startsWith(key)) {
              keysToRemove.push(storageKey);
            }
          }
          keysToRemove.forEach((k) => localStorage.removeItem(k));
        });
      },
    };
  },

  /**
   * Remove expired entries for a given prefix
   * @param {string} prefix - Cache key prefix
   */
  evictExpired(prefix) {
    try {
      const now = Date.now();
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i);
        if (!storageKey || !storageKey.startsWith(prefix)) continue;
        try {
          const entry = JSON.parse(localStorage.getItem(storageKey));
          const ttl = this.TTL_MS;
          if (entry && entry.__ts && now - entry.__ts > ttl) {
            keysToRemove.push(storageKey);
          }
        } catch (e) {
          // Corrupted — remove it
          keysToRemove.push(storageKey);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch (e) {
      // Non-critical — silently ignore
    }
  },

  /**
   * Create in-memory cache with TTL (fallback when localStorage unavailable)
   * @returns {object} Cache object
   */
  createMemoryCache() {
    const cache = {};
    const self = this;
    return {
      get(cacheKey, defaultValue, events = {}) {
        return Promise.resolve().then(() => {
          const keyStr = JSON.stringify(cacheKey);
          if (keyStr in cache) {
            const entry = cache[keyStr];
            // Check TTL for memory cache too
            const ttl = self.TTL_MS;
            if (entry.__ts && Date.now() - entry.__ts > ttl) {
              delete cache[keyStr];
            } else {
              return entry.__data !== undefined ? entry.__data : entry;
            }
          }
          const value = typeof defaultValue === "function" ? defaultValue() : defaultValue;
          return Promise.resolve(value).then((resolvedValue) => {
            if (events.miss) {
              events.miss(resolvedValue);
            }
            return resolvedValue;
          });
        });
      },
      set(cacheKey, value) {
        return Promise.resolve().then(() => {
          cache[JSON.stringify(cacheKey)] = { __ts: Date.now(), __data: value };
          return value;
        });
      },
      delete(cacheKey) {
        return Promise.resolve().then(() => {
          delete cache[JSON.stringify(cacheKey)];
        });
      },
      clear() {
        return Promise.resolve().then(() => {
          Object.keys(cache).forEach((k) => delete cache[k]);
        });
      },
    };
  },

  /**
   * Create null cache (no caching - used when ?nocache is present)
   * @returns {object} Cache object that doesn't cache
   */
  createNullCache() {
    return {
      get(cacheKey, defaultValue, events = {}) {
        const value = typeof defaultValue === "function" ? defaultValue() : defaultValue;
        return Promise.resolve(value).then((resolvedValue) => {
          if (events.miss) {
            events.miss(resolvedValue);
          }
          return resolvedValue;
        });
      },
      set(cacheKey, value) {
        return Promise.resolve(value);
      },
      delete() {
        return Promise.resolve();
      },
      clear() {
        return Promise.resolve();
      },
    };
  },

  /**
   * Clear all Algolia cache entries from localStorage (all versions)
   */
  clearAll() {
    const prefix = `algolia-responses-v`;
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
    console.log(`[AlgoliaCacheConfig] Cleared ${keysToRemove.length} cache entries`);
  },
};
